import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import type { IRecord, ISearchHit, IUserRef } from '@platform/core/types';
import { useGetCatalogQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { useForeignRecords } from '../../tabs/related/useForeignRecords';
import { AdvancedSearchDialog } from './AdvancedSearchDialog';
import type { ILinkSearchDialogProps } from './ILinkSearchDialogProps';
import { linkCandidateTypes } from './linkCandidateTypes';
import { useDebouncedValue } from './useDebouncedValue';
import { LINK_LIMIT, useLinkCandidates } from './useLinkCandidates';

const NONE: string[] = [];

/** Вибраний запис у формі збігу глобального пошуку (його чекають «Пов’язані елементи» й вставка посилання в статтю). */
const hitOf = (ws: string, r: IRecord): ISearchHit => ({
  recordId: r.id, workspaceId: ws, module: r.module, recordType: r.recordType, code: r.code, display: r.display, title: r.title, status: r.status, rank: 0,
});

/**
 * «Знайдіть елемент для зв’язку» (UIUX/forms/related-items; прототип lkAdv kind record): записи всіх модулів простору в порядку модулів,
 * одразу без пошуку (перші 200, «N елементів · показано перші 200»), пошук за назвою або кодом, чипи «Усі модулі» й кожного модуля простору;
 * колонки Модуль (вид запису) · ID · Назва · Статус · Виконавець; без самого запису й уже пов'язаних; клік по рядку вибирає запис.
 * Для кнопки «…» вкладки «Пов’язані елементи» і вставки посилання в статтю.
 */
export function LinkSearchDialog({ ws, title, excludeIds, onPick, onClose }: ILinkSearchDialogProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('');
  const q = useDebouncedValue(query.trim(), 250);
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws });
  const types = useMemo(() => linkCandidateTypes(catalog, lang), [catalog, lang]);
  const found = useLinkCandidates(ws, q, filter, excludeIds ?? NONE);
  const foreign = useForeignRecords(ws, useMemo(() => found.rows.map((r) => r.module), [found.rows]));
  const rows = found.rows.map((r) => {
    const who = r.values['assignee'] as IUserRef | null | undefined;
    return {
      key: r.id,
      cells: [
        { text: foreign.kindLabel(r.module, r.recordType, r.values), muted: true }, { text: String(r.code), medium: true, muted: true },
        { text: r.title || t('fieldControls.untitled'), strong: true }, { text: r.status ? foreign.status(r.module, r.recordType, r.status).label : '—' },
        { text: who?.name ?? t('fieldControls.unassigned'), muted: true },
      ],
      onPick: () => onPick(hitOf(ws, r)),
    };
  });
  const chips = [{ key: '', label: t('fieldControls.allModules') }, ...types.map((x) => ({ key: x.rt, label: x.label }))].map((c) => (
    <button key={c.key || '_all'} type="button" className={cx('lkadv__chip', filter === c.key && 'is-on')} aria-pressed={filter === c.key} onClick={() => setFilter(c.key)}>
      {c.label}
    </button>
  ));
  const count = t('fieldControls.items', { count: found.total }) + (found.total > LINK_LIMIT ? ` · ${t('fieldControls.showingFirst', { count: LINK_LIMIT })}` : '');
  return (
    <AdvancedSearchDialog title={title ?? t('fieldControls.findItem')} count={count} query={query} onQuery={setQuery} loading={found.loading} chips={<>{chips}</>}
      columns={[t('fieldControls.colModule'), 'ID', t('fieldControls.colTitle'), t('fieldControls.colStatus'), t('fieldControls.colAssignee')]}
      template="110px 100px minmax(220px,2fr) 130px 150px" rows={rows} onClose={onClose} />
  );
}
